/**
 * Création et correction d'une machine.
 *
 * Marque et fabricant sont deux listes distinctes de tiers, conformément au
 * CDC §6.3 : la même société peut tenir les deux rôles, ou un seul.
 *
 * Le QR code n'apparaît pas : il est généré à la création et ne change plus,
 * l'étiquette étant physique.
 *
 * Depuis le 2026-10-01, ce formulaire porte AUSSI les caractéristiques
 * techniques (attributs configurables par famille) : une plaque signalétique
 * mélange identification et caractéristiques, l'écran de saisie ne doit pas
 * forcer deux parcours différents pour une seule photo. Les deux restent deux
 * écritures distinctes côté API (`updateAsset` pour les champs fixes,
 * `setAssetAttribute` par attribut modifié) — invisible pour qui remplit le
 * formulaire. Uniquement en correction : une machine neuve n'a pas encore de
 * famille assignée, donc pas de définitions d'attributs à proposer.
 */
import { useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  api,
  type Asset,
  type AssetAttribute,
  type AssetStatus,
  type Party,
  type Site,
} from '../api/client';
import { AvertissementDoublons } from './AvertissementDoublons';
import { ComboRecherche } from './ComboRecherche';
import { Champ, ErreurFormulaire, Ligne, Panneau } from './Formulaire';
import { useDoublons } from './useDoublons';
import { doitInterrogerMachine, doublonsDeLErreur, type Doublon } from '../domaine/doublons';
import { AUTRE_FAMILLE, avertissementFamille, famillesProposees, type FamilleComptee } from '../domaine/familles-machine';
import { STATUTS_MACHINE, STATUT_MACHINE, ordonnerCaracteristiques } from '../domaine/machines';

interface Saisie {
  assetType: string;
  label: string;
  model: string;
  manufacturerReference: string;
  clientDesignation: string;
  serialNumber: string;
  siteId: string;
  location: string;
  brandPartyId: string;
  manufacturerPartyId: string;
  ownerPartyId: string;
  status: AssetStatus;
  commissionedAt: string;
  notes: string;
}

interface SaisieAttribut {
  valeur: string;
  nonCommunicable: boolean;
  motif: string;
}

/** Les champs qui IDENTIFIENT une machine : changer l'un d'eux invalide l'avertissement de doublon déjà affiché. */
const CHAMPS_IDENTITE: readonly (keyof Saisie)[] = [
  'serialNumber',
  'model',
  'clientDesignation',
  'brandPartyId',
  'siteId',
];

function saisieAttributInitiale(a: AssetAttribute): SaisieAttribut {
  return {
    valeur: a.completeness === 'PROVIDED' ? (a.value ?? '') : '',
    nonCommunicable: a.completeness === 'UNAVAILABLE',
    motif: a.unavailableReason ?? '',
  };
}

export function FormulaireMachine({
  machine,
  siteImpose,
  onFermer,
  onEnregistre,
}: {
  /** `null` = création. */
  machine: Asset | null;
  siteImpose?: string | undefined;
  onFermer: () => void;
  onEnregistre: (machine: Asset) => void;
}) {
  const [sites, setSites] = useState<Site[]>([]);
  const [marques, setMarques] = useState<Party[]>([]);
  const [fabricants, setFabricants] = useState<Party[]>([]);
  const [tiers, setTiers] = useState<Party[]>([]);
  const [famillesComptees, setFamillesComptees] = useState<readonly FamilleComptee[]>([]);
  // « Autre… » choisi : une famille nouvelle se tape à la main.
  const [familleLibre, setFamilleLibre] = useState(false);
  const [saisie, setSaisie] = useState<Saisie>(() => ({
    assetType: machine?.assetType ?? '',
    label: machine?.label ?? '',
    model: machine?.model ?? '',
    manufacturerReference: machine?.manufacturerReference ?? '',
    clientDesignation: machine?.clientDesignation ?? '',
    serialNumber: machine?.serialNumber ?? '',
    siteId: machine?.siteId ?? siteImpose ?? '',
    location: machine?.location ?? '',
    brandPartyId: machine?.brandPartyId ?? '',
    manufacturerPartyId: machine?.manufacturerPartyId ?? '',
    ownerPartyId: machine?.ownerPartyId ?? '',
    status: machine?.status ?? 'IN_SERVICE',
    commissionedAt: machine?.commissionedAt ?? '',
    notes: machine?.notes ?? '',
  }));
  const [attributsSaisie, setAttributsSaisie] = useState<Record<string, SaisieAttribut>>(() =>
    Object.fromEntries((machine?.attributes ?? []).map((a) => [a.definitionId, saisieAttributInitiale(a)])),
  );
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [enCours, setEnCours] = useState(false);
  // Les fiches que le SERVEUR a rapprochées en refusant l'enregistrement (HTTP 409) : elles priment sur la
  // consultation faite pendant la saisie, et disparaissent dès qu'un champ d'identité change.
  const [doublonsServeur, setDoublonsServeur] = useState<readonly Doublon[] | null>(null);

  const identiteCourante = {
    serialNumber: saisie.serialNumber,
    model: saisie.model,
    clientDesignation: saisie.clientDesignation,
    brandPartyId: saisie.brandPartyId,
    siteId: saisie.siteId,
  };
  const identiteInitiale = machine
    ? {
        serialNumber: machine.serialNumber ?? '',
        model: machine.model ?? '',
        clientDesignation: machine.clientDesignation ?? '',
        brandPartyId: machine.brandPartyId ?? '',
        siteId: machine.siteId ?? '',
      }
    : null;
  const doublonsEnSaisie = useDoublons(
    doitInterrogerMachine(identiteCourante, identiteInitiale)
      ? () =>
          api
            .doublonsMachine({
              serialNumber: saisie.serialNumber,
              brandPartyId: saisie.brandPartyId || undefined,
              model: saisie.model,
              siteId: saisie.siteId || undefined,
              clientDesignation: saisie.clientDesignation,
              excludeId: machine?.id,
            })
            .then((r) => r.doublons)
      : null,
    JSON.stringify(identiteCourante),
  );
  const doublons = doublonsServeur ?? doublonsEnSaisie;

  useEffect(() => {
    // Les listes de rattachement sont chargées en parallèle : leur échec ne doit
    // pas empêcher de saisir le reste de la fiche.
    void api.sites().then(setSites).catch(() => undefined);
    void api.parties(undefined, 'BRAND').then(setMarques).catch(() => undefined);
    void api.parties(undefined, 'MANUFACTURER').then(setFabricants).catch(() => undefined);
    // Le propriétaire n'a pas de rôle dédié : tout tiers peut posséder une machine.
    void api.parties().then(setTiers).catch(() => undefined);
    // Les familles déjà employées par l'espace forment la liste ; le produit n'impose aucune nomenclature (CDC §1).
    void api
      .assetFacets()
      .then((f) => setFamillesComptees(f.byNature))
      .catch(() => undefined);
  }, []);

  const modifier = <K extends keyof Saisie>(cle: K, valeur: Saisie[K]) => {
    if (CHAMPS_IDENTITE.includes(cle)) setDoublonsServeur(null);
    setSaisie((s) => ({ ...s, [cle]: valeur }));
  };

  // La valeur actuelle reste affichée même si la liste n'est pas (encore)
  // chargée, si le tiers a été archivé depuis, ou s'il n'a pas le rôle attendu :
  // sans cela le champ paraîtrait vide alors que l'enregistrement le conserverait.
  const optionsMarque = avecValeurActuelle(marques, machine?.brandPartyId, machine?.brandName);
  const optionsFabricant = avecValeurActuelle(
    fabricants,
    machine?.manufacturerPartyId,
    machine?.manufacturerName,
  );
  const optionsProprietaire = avecValeurActuelle(tiers, machine?.ownerPartyId, machine?.ownerName);

  // Même ordre que la fiche : le formulaire ne doit pas redistribuer ce que
  // l'écran de lecture a rangé (cf. `ordonnerCaracteristiques`).
  const caracteristiques = machine ? ordonnerCaracteristiques(machine.attributes) : null;
  const caracteristiquesOrdonnees = caracteristiques
    ? [...caracteristiques.avant, ...caracteristiques.apres, ...caracteristiques.restants]
    : [];

  /** `confirmer` : l'utilisateur a vu l'avertissement de doublon et affirme que c'est une autre machine. */
  const enregistrer = async (e: FormEvent | undefined, confirmer = false) => {
    e?.preventDefault();
    if (!saisie.assetType.trim()) {
      setErreur(new Error('La famille est obligatoire : compresseur, sécheur, groupe froid…'));
      return;
    }

    setEnCours(true);
    setErreur(null);
    try {
      const corps: Record<string, unknown> = {
        assetType: saisie.assetType.trim(),
        label: vide(saisie.label),
        model: vide(saisie.model),
        manufacturerReference: vide(saisie.manufacturerReference),
        clientDesignation: vide(saisie.clientDesignation),
        serialNumber: vide(saisie.serialNumber),
        location: vide(saisie.location),
        notes: vide(saisie.notes),
        siteId: saisie.siteId || null,
        brandPartyId: saisie.brandPartyId || null,
        manufacturerPartyId: saisie.manufacturerPartyId || null,
        ownerPartyId: saisie.ownerPartyId || null,
        status: saisie.status,
        commissionedAt: vide(saisie.commissionedAt),
        ...(confirmer ? { confirmerDoublon: true } : {}),
      };

      let resultat = machine
        ? await api.updateAsset(machine.id, corps)
        : await api.createAsset(
            Object.fromEntries(Object.entries(corps).filter(([, v]) => v !== null)),
          );

      // Caractéristiques techniques : une écriture par attribut RÉELLEMENT
      // modifié depuis l'ouverture du formulaire — jamais une resaisie
      // silencieuse de ce qui n'a pas bougé.
      if (machine) {
        for (const a of machine.attributes) {
          const s = attributsSaisie[a.definitionId];
          if (!s) continue;
          if (s.nonCommunicable) {
            const motif = s.motif.trim();
            if (motif === '' || (a.completeness === 'UNAVAILABLE' && a.unavailableReason === motif)) {
              continue;
            }
            resultat = await api.setAssetAttribute(machine.id, a.definitionId, {
              completeness: 'UNAVAILABLE',
              unavailableReason: motif,
            });
          } else {
            const valeur = s.valeur.trim();
            if (valeur === '' || (a.completeness === 'PROVIDED' && a.value === valeur)) continue;
            resultat = await api.setAssetAttribute(machine.id, a.definitionId, {
              completeness: 'PROVIDED',
              value: valeur,
            });
          }
        }
      }

      onEnregistre(resultat);
      onFermer();
    } catch (e) {
      // Un doublon probable n'est pas une panne : on montre les fiches existantes et on attend la décision.
      const rapprochees = doublonsDeLErreur(e);
      if (rapprochees) {
        setDoublonsServeur(rapprochees);
        setErreur(null);
      } else {
        setErreur(e instanceof ApiError ? e : new Error('Enregistrement impossible.'));
      }
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Panneau
      titre={machine ? 'Corriger la machine' : 'Nouvelle machine'}
      sousTitre={
        machine
          ? `QR ${machine.qrCode} — l’étiquette ne change pas.`
          : 'Un QR code sera généré automatiquement.'
      }
      onFermer={onFermer}
      actions={
        <>
          <button className="secondaire" onClick={onFermer} disabled={enCours}>
            Annuler
          </button>
          <button onClick={(e) => void enregistrer(e)} disabled={enCours}>
            {enCours ? 'Enregistrement…' : machine ? 'Enregistrer' : 'Créer la machine'}
          </button>
        </>
      }
    >
      <form onSubmit={(e) => void enregistrer(e)}>
        <ErreurFormulaire
          message={erreur?.message ?? null}
          details={erreur instanceof ApiError ? erreur.details : undefined}
        />

        <AvertissementDoublons
          objet="machine"
          doublons={doublons}
          correction={Boolean(machine)}
          enCours={enCours}
          defiler={doublonsServeur !== null}
          onConfirmer={() => void enregistrer(undefined, true)}
        />

        <Ligne>
          <Champ libelle="Famille" requis aide="Le genre de machine : choisissez-le ; « Autre… » pour une famille nouvelle.">
            <select
              autoFocus
              value={familleLibre ? AUTRE_FAMILLE : saisie.assetType}
              onChange={(e) => {
                if (e.target.value === AUTRE_FAMILLE) {
                  setFamilleLibre(true);
                  modifier('assetType', '');
                } else {
                  setFamilleLibre(false);
                  modifier('assetType', e.target.value);
                }
              }}
            >
              <option value="">Choisir…</option>
              {famillesProposees(famillesComptees, familleLibre ? '' : saisie.assetType).map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
              <option value={AUTRE_FAMILLE}>Autre…</option>
            </select>
            {familleLibre && (
              <input
                type="text"
                placeholder="Nouvelle famille, par exemple Surpresseur"
                value={saisie.assetType}
                onChange={(e) => modifier('assetType', e.target.value)}
              />
            )}
            {avertissementFamille(saisie.assetType) && <div className="message avertissement">{avertissementFamille(saisie.assetType)}</div>}
          </Champ>
          <Champ libelle="État">
            <select
              value={saisie.status}
              onChange={(e) => modifier('status', e.target.value as AssetStatus)}
            >
              {STATUTS_MACHINE.map((s) => (
                <option key={s} value={s}>
                  {STATUT_MACHINE[s].texte}
                </option>
              ))}
            </select>
          </Champ>
        </Ligne>

        <Ligne>
          <Champ libelle="Désignation" aide="Le nom que lui donnent vos techniciens.">
            <input
              type="text"
              value={saisie.label}
              onChange={(e) => modifier('label', e.target.value)}
            />
          </Champ>
          <Champ libelle="Désignation client" aide="Le nom que lui donne le client : « C1 », « AIR01 ».">
            <input
              type="text"
              value={saisie.clientDesignation}
              onChange={(e) => modifier('clientDesignation', e.target.value)}
            />
          </Champ>
        </Ligne>

        <Ligne>
          <Champ libelle="Modèle">
            <input
              type="text"
              value={saisie.model}
              onChange={(e) => modifier('model', e.target.value)}
            />
          </Champ>
          <Champ libelle="Référence" aide="Code article du constructeur, ex. « ESD.3 ».">
            <input
              type="text"
              value={saisie.manufacturerReference}
              onChange={(e) => modifier('manufacturerReference', e.target.value)}
            />
          </Champ>
          <Champ
            libelle="Numéro de série"
            aide="Sert à repérer les doublons, à l’import comme à la saisie."
          >
            <input
              type="text"
              value={saisie.serialNumber}
              onChange={(e) => modifier('serialNumber', e.target.value)}
            />
          </Champ>
        </Ligne>

        <Ligne>
          <Champ libelle="Marque">
            <select
              value={saisie.brandPartyId}
              onChange={(e) => modifier('brandPartyId', e.target.value)}
            >
              <option value="">Aucune</option>
              {optionsMarque.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </Champ>
          <Champ libelle="Fabricant" aide="Distinct de la marque (CDC §6.3).">
            <select
              value={saisie.manufacturerPartyId}
              onChange={(e) => modifier('manufacturerPartyId', e.target.value)}
            >
              <option value="">Aucun</option>
              {optionsFabricant.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          </Champ>
        </Ligne>

        <Champ
          libelle="Propriétaire"
          aide="À renseigner seulement si la machine n’appartient pas au client du site (location, mise à disposition)."
        >
          <ComboRecherche
            options={optionsProprietaire}
            valeur={saisie.ownerPartyId || null}
            onChange={(id) => modifier('ownerPartyId', id ?? '')}
            placeholder="Chercher un tiers…"
            viderLibelle="Aucun propriétaire distinct"
          />
        </Champ>

        <Champ libelle="Site" aide="Peut rester vide : la machine sera signalée sans implantation.">
          <select
            value={saisie.siteId}
            onChange={(e) => modifier('siteId', e.target.value)}
            disabled={Boolean(siteImpose)}
          >
            <option value="">Sans site</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} — {s.customerName}
              </option>
            ))}
          </select>
        </Champ>

        <Ligne>
          <Champ libelle="Emplacement" aide="« Local compresseurs, niveau -1 »">
            <input
              type="text"
              value={saisie.location}
              onChange={(e) => modifier('location', e.target.value)}
            />
          </Champ>
          <Champ libelle="Mise en service">
            <input
              type="date"
              value={saisie.commissionedAt}
              onChange={(e) => modifier('commissionedAt', e.target.value)}
            />
          </Champ>
        </Ligne>

        {machine && caracteristiquesOrdonnees.length > 0 && (
          <>
            <h3>Caractéristiques techniques</h3>
            {caracteristiquesOrdonnees.map((a) => (
              <ChampAttribut
                key={a.definitionId}
                attribut={a}
                saisie={attributsSaisie[a.definitionId] ?? saisieAttributInitiale(a)}
                onChange={(s) =>
                  setAttributsSaisie((tout) => ({ ...tout, [a.definitionId]: s }))
                }
              />
            ))}
          </>
        )}

        <Champ libelle="Remarques">
          <textarea
            rows={3}
            value={saisie.notes}
            onChange={(e) => modifier('notes', e.target.value)}
          />
        </Champ>
      </form>
    </Panneau>
  );
}

/**
 * Une caractéristique dans le formulaire unique de correction : valeur ou
 * bascule « non communicable » avec motif obligatoire, même mécanique que
 * l'ancienne saisie en ligne, déplacée ici (2026-10-01).
 */
function ChampAttribut({
  attribut,
  saisie,
  onChange,
}: {
  attribut: AssetAttribute;
  saisie: SaisieAttribut;
  onChange: (s: SaisieAttribut) => void;
}) {
  return (
    <Champ
      libelle={attribut.label}
      {...(attribut.expected && attribut.completeness === 'MISSING'
        ? { aide: 'attendu sur cette famille' }
        : {})}
    >
      {saisie.nonCommunicable ? (
        <div className="saisie-ligne">
          <input
            type="text"
            placeholder="Motif — obligatoire"
            value={saisie.motif}
            onChange={(e) => onChange({ ...saisie, motif: e.target.value })}
          />
          <button
            type="button"
            className="secondaire discret"
            onClick={() => onChange({ ...saisie, nonCommunicable: false, motif: '' })}
          >
            Annuler
          </button>
        </div>
      ) : (
        <div className="saisie-ligne">
          {attribut.dataType === 'ENUM' && attribut.options.length > 0 ? (
            <select
              value={saisie.valeur}
              onChange={(e) => onChange({ ...saisie, valeur: e.target.value })}
            >
              <option value="">Choisir…</option>
              {attribut.options.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              placeholder={attribut.unit ? `Valeur en ${attribut.unit}` : 'Valeur'}
              value={saisie.valeur}
              onChange={(e) => onChange({ ...saisie, valeur: e.target.value })}
            />
          )}
          <button
            type="button"
            className="secondaire discret"
            title="La donnée ne sera jamais obtenue : plaque illisible, machine inaccessible…"
            onClick={() => onChange({ ...saisie, nonCommunicable: true })}
          >
            Non communicable
          </button>
        </div>
      )}
    </Champ>
  );
}

function vide(v: string): string | null {
  return v.trim() === '' ? null : v.trim();
}

/**
 * Options d'une liste de tiers, avec la valeur ACTUELLE de la machine ajoutée en
 * tête si elle n'y figure pas (liste pas encore chargée, tiers archivé, rôle
 * différent). Le formulaire garde ainsi toujours ce qu'il affiche.
 */
function avecValeurActuelle(
  tiers: readonly Party[],
  id: string | null | undefined,
  nom: string | null | undefined,
): { id: string; label: string }[] {
  const options = tiers.map((t) => ({ id: t.id, label: t.legalName }));
  if (id && nom && !options.some((o) => o.id === id)) {
    options.unshift({ id, label: nom });
  }
  return options;
}
